import React from "react";
import isNil from "lodash/isNil";
import { useForm, FormProvider } from "react-hook-form";
import { Button, Typography, Box } from "@mui/material";

import { useAnalytics } from "globals/hooks";
import { WarningTriangleIcon } from "design-system/icons";
import { errorRed, granite, warningBackground } from "design-system/colors";
import { useTermsAndConditions } from "pages/new/hooks/useTermsAndConditions";
import { useConsentSignature } from "pages/new/hooks/useConsentSignature";
import { useSignTermsOfService } from "../form/components/TermsAndConditionsBlock/hooks/useSignTermsOfService";
import { useCancellationPolicies } from "../form/components/TermsAndConditionsBlock/hooks/useCancellationPolicies";
import useIntroTermsAndConditions from "pages/new/hooks/useIntroTermsAndConditions";
import ConsentSignatureDialog from "../form/components/TermsAndConditionsBlock/components/ConsentSignatureDialog/ConsentSignatureDialog";
import {
  TermsSignatureFormState,
  termsSignatureFormDefaultValues,
} from "../form/components/TermsAndConditionsBlock/termsSignatureSchema";
import { Request } from "types";

type TermsSignButtonProps = {
  request: Request;
  refetchRequest?: () => void;
};

const FORM_ID = "terms-signature-form";

export default function TermsSignButton({ request, refetchRequest }: TermsSignButtonProps) {
  // hooks
  const { track } = useAnalytics();
  const { cancellationPolicies } = useCancellationPolicies({
    trips: request.trips,
  });
  const consentSignature = useConsentSignature({ trips: request.trips });
  const { signTermsOfService, isSubmitting } = useSignTermsOfService();
  const terms = useTermsAndConditions();
  const intro = useIntroTermsAndConditions();

  // state
  const hasSignedTos = !isNil(request.reservationConfirmationSignatureLog);

  const methods = useForm<TermsSignatureFormState>({
    defaultValues: termsSignatureFormDefaultValues,
    mode: "onSubmit",
  });

  const { control, watch, setValue, handleSubmit } = methods;

  // event handlers
  const handleTermsAndConditionsOpen = () => {
    consentSignature.handleConsentSignatureDialogOpen();
  };

  const onSubmitTermsForm = async (data: TermsSignatureFormState) => {
    try {
      track("reservationConfirmation_signatureSubmitted", {
        signature: data.printedName || "No signature provided",
      });

      const termNameAndDescription = terms?.map((term) => ({
        name: term.name,
        description: term.description,
      }));

      const success = await signTermsOfService({
        request: request,
        printedName: data.printedName || "",
        intro,
        terms: termNameAndDescription,
        cancellationPolicies,
      });

      if (success) {
        consentSignature.handleConsentSignatureDialogClose();
        
        // Refetch the request to update the UI with signed status
        if (refetchRequest) {
          refetchRequest();
        }
      }
    } catch (error) {
      console.error("Error in signature submission:", error);
    }
  };

  // Function to handle form validation errors
  const onSubmitError = (errors: any) => {
    console.error("Form validation errors:", errors);
  };

  return (
    <FormProvider {...methods}>
      <Box display="flex" alignItems="center">
        {hasSignedTos ? (
          <Typography variant="body2" style={{ color: granite }}>
            Terms & Conditions Signed
          </Typography>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={1}
          >
            <Button
              variant="outlined"
              onClick={handleTermsAndConditionsOpen}
              startIcon={<WarningTriangleIcon size="small" />}
              sx={{
                backgroundColor: warningBackground,
                color: errorRed,
                borderColor: errorRed,
                "&:hover": {
                  backgroundColor: warningBackground,
                  opacity: 0.7,
                  borderColor: errorRed,
                },
                "& .MuiButton-startIcon svg path": {
                  stroke: errorRed,
                },
              }}
            >
              Sign Terms & Conditions
            </Button>
            <Typography variant="h4" style={{ color: errorRed }}>
              Your trip is not fully confirmed until you sign the terms and
              conditions.
            </Typography>
          </Box>
        )}

        <form
          id={FORM_ID}
          noValidate
          onSubmit={handleSubmit(onSubmitTermsForm, onSubmitError)}
        >
          <ConsentSignatureDialog
            control={control}
            watch={watch}
            setValue={setValue}
            formId={FORM_ID}
            isSubmitting={isSubmitting}
            isDialogOpen={consentSignature.consentSignatureDialogOpen}
            onClose={consentSignature.handleConsentSignatureDialogClose}
            consentSignature={consentSignature.consentSignature}
            userConsentAgreement={consentSignature.isAgreementChecked}
            onConsentSignatureChange={
              consentSignature.handleConsentSignatureChange
            }
            onUserConsentAgreementToggle={
              consentSignature.handleUserConsentAgreementToggle
            }
            selectedVehicleIds={consentSignature.selectedVehicleIds}
          />
        </form>
      </Box>
    </FormProvider>
  );
}
